import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckSquare,
  DollarSign,
  Package,
  Check,
  X,
  Loader2,
} from 'lucide-react';
import Badge from '@/components/ui/Badge';
import Card from '@/components/ui/Card';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import api from '@/lib/api';
import { useUIStore } from '@/stores/ui-store';
import { cn, formatApprovalDiff } from '@/lib/utils';

interface Approval {
  id: string;
  approval_type: string;
  status: string;
  entity_type: string;
  entity_id: string;
  task_id: string | null;
  proposed_changes: Record<string, unknown>;
  current_values?: Record<string, unknown>;
  created_at: string;
}

interface PaginationData {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

const TABS = [
  { key: '', label: 'All' },
  { key: 'price_change', label: 'Price Changes' },
  { key: 'product_publish', label: 'Product Listings' },
  { key: 'supplier_action', label: 'Supplier Actions' },
];

const typeBadge: Record<string, { variant: 'yellow' | 'blue' | 'green' | 'gray'; label: string }> = {
  price_change: { variant: 'yellow', label: 'Price Change' },
  product_publish: { variant: 'blue', label: 'Product Publish' },
  supplier_action: { variant: 'green', label: 'Supplier Action' },
};

export default function AgentApprovals() {
  const addToast = useUIStore((s) => s.addToast);
  const [approvals, setApprovals] = useState<Approval[]>([]);
  const [pagination, setPagination] = useState<PaginationData>({ page: 1, limit: 25, total: 0, total_pages: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [batchAction, setBatchAction] = useState<'approve' | 'reject' | null>(null);
  const [showBatchModal, setShowBatchModal] = useState(false);

  const fetchApprovals = async (page = 1) => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('limit', '25');
      params.set('status', 'pending');
      if (typeFilter) params.set('type', typeFilter);
      const res = await api.get<{ approvals: Approval[]; pagination: PaginationData }>(
        `/admin/agent/approvals?${params.toString()}`
      );
      setApprovals(res.approvals);
      setPagination(res.pagination);
      setSelected(new Set());
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to load approvals', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchApprovals(1);
  }, [typeFilter]);

  const handleApprove = async (id: string) => {
    setProcessingId(id);
    try {
      await api.post(`/admin/agent/approvals/${id}/approve`);
      setApprovals((prev) => prev.filter((a) => a.id !== id));
      addToast('Approved', 'success');
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to approve', 'error');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (id: string) => {
    setProcessingId(id);
    try {
      await api.post(`/admin/agent/approvals/${id}/reject`);
      setApprovals((prev) => prev.filter((a) => a.id !== id));
      addToast('Rejected', 'success');
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to reject', 'error');
    } finally {
      setProcessingId(null);
    }
  };

  const handleBatchAction = async () => {
    if (!batchAction || selected.size === 0) return;
    try {
      await api.post('/admin/agent/approvals/batch', { ids: [...selected], action: batchAction });
      setApprovals((prev) => prev.filter((a) => !selected.has(a.id)));
      addToast(`${selected.size} approvals ${batchAction === 'approve' ? 'approved' : 'rejected'}`, 'success');
      setSelected(new Set());
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to process batch', 'error');
    } finally {
      setShowBatchModal(false);
      setBatchAction(null);
    }
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const formatProposedChanges = (approval: Approval): string => {
    const c = approval.proposed_changes;
    const cur = approval.current_values || {};
    if (approval.approval_type === 'price_change') {
      if (c.new_price != null) {
        return `Price ${formatApprovalDiff('new_price', cur.new_price, c.new_price)}`;
      }
      return JSON.stringify(c);
    }
    if (approval.approval_type === 'product_publish') {
      return 'Publish product to store';
    }
    return JSON.stringify(c);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Approvals</h1>
        <p className="text-gray-600 mt-1">Review and approve changes proposed by the AI agent.</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setTypeFilter(tab.key)}
            className={cn(
              'px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px',
              typeFilter === tab.key
                ? 'border-green-600 text-green-700'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Batch actions */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 bg-gray-50 rounded-lg px-4 py-2.5 border border-gray-200">
          <span className="text-sm text-gray-600">{selected.size} selected</span>
          <button
            onClick={() => { setBatchAction('approve'); setShowBatchModal(true); }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700"
          >
            <Check className="w-3.5 h-3.5" />
            Approve Selected
          </button>
          <button
            onClick={() => { setBatchAction('reject'); setShowBatchModal(true); }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-700 rounded-md text-sm font-medium hover:bg-red-100 border border-red-200"
          >
            <X className="w-3.5 h-3.5" />
            Reject Selected
          </button>
        </div>
      )}

      {/* Approval List */}
      {isLoading ? (
        <div className="p-8 text-center text-gray-500">Loading...</div>
      ) : approvals.length === 0 ? (
        <Card padding="lg">
          <div className="text-center py-8">
            <CheckSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <h3 className="text-lg font-semibold text-gray-900 mb-1">No pending approvals</h3>
            <p className="text-gray-500 text-sm">All clear! Approvals will appear here when the agent proposes changes.</p>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {approvals.map((approval) => {
            const badge = typeBadge[approval.approval_type] ?? { variant: 'gray' as const, label: approval.approval_type };
            const isProcessing = processingId === approval.id;
            return (
              <Card key={approval.id} padding="md">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={selected.has(approval.id)}
                    onChange={() => toggleSelect(approval.id)}
                    className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500 mt-1"
                  />
                  <div className={cn(
                    'p-2 rounded-lg shrink-0',
                    approval.approval_type === 'price_change' ? 'bg-yellow-100' : 'bg-blue-100'
                  )}>
                    {approval.approval_type === 'price_change' ? (
                      <DollarSign className="w-5 h-5 text-yellow-700" />
                    ) : (
                      <Package className="w-5 h-5 text-blue-700" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant={badge.variant}>{badge.label}</Badge>
                      <span className="text-xs text-gray-400">
                        {new Date(approval.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-800">{formatProposedChanges(approval)}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {approval.entity_type}: {approval.entity_id.slice(0, 8)}...
                      {approval.task_id && (
                        <>
                          {' · '}
                          <Link to={`/admin/agent/tasks/${approval.task_id}`} className="text-blue-600 hover:text-blue-800">
                            View task →
                          </Link>
                        </>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => handleApprove(approval.id)}
                      disabled={isProcessing}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                    >
                      {isProcessing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                      Approve
                    </button>
                    <button
                      onClick={() => handleReject(approval.id)}
                      disabled={isProcessing}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-white text-red-700 border border-red-200 rounded-md text-sm font-medium hover:bg-red-50 disabled:opacity-50"
                    >
                      <X className="w-3.5 h-3.5" />
                      Reject
                    </button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">
            Page {pagination.page} of {pagination.total_pages}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => fetchApprovals(pagination.page - 1)}
              disabled={pagination.page <= 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => fetchApprovals(pagination.page + 1)}
              disabled={pagination.page >= pagination.total_pages}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Batch confirmation modal */}
      <Modal
        isOpen={showBatchModal}
        onClose={() => { setShowBatchModal(false); setBatchAction(null); }}
        title={`${batchAction === 'approve' ? 'Approve' : 'Reject'} ${selected.size} items?`}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            This will {batchAction} {selected.size} pending approval{selected.size > 1 ? 's' : ''}.
            {batchAction === 'approve' && ' Approved changes will be applied immediately.'}
          </p>
          <ul
            data-testid="batch-preview-list"
            className="max-h-56 overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-100 bg-gray-50"
          >
            {approvals.filter((a) => selected.has(a.id)).map((a) => {
              const b = typeBadge[a.approval_type] ?? { variant: 'gray' as const, label: a.approval_type };
              return (
                <li key={a.id} className="flex items-start gap-2 px-3 py-2 text-xs">
                  {a.approval_type === 'price_change' ? (
                    <DollarSign className="w-3.5 h-3.5 text-yellow-600 mt-0.5 shrink-0" />
                  ) : (
                    <Package className="w-3.5 h-3.5 text-blue-600 mt-0.5 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-gray-700">{b.label}</span>
                    <span className="text-gray-500"> · {formatProposedChanges(a)}</span>
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => { setShowBatchModal(false); setBatchAction(null); }}>
              Cancel
            </Button>
            <Button
              variant={batchAction === 'approve' ? 'primary' : 'danger'}
              onClick={handleBatchAction}
            >
              {batchAction === 'approve' ? 'Approve All' : 'Reject All'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
